import {
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import * as moment from 'moment';

import { MaxDate } from '@sd-portal/shared/util';
import { Airport } from '@sd-portal/shared/airport';

import { LegFuelForm } from './leg-fuel-form.model';
import { LegOooiForm } from './leg-oooi-form.model';
import { LegHobbsForm, validateHobbs } from './leg-hobbs-form.model';
import { Leg } from './leg.model';
import { AutomatedFlightDataForm } from './automated-flight-data-form.model';
import { PartOperation } from './enums/part-operation.enum';

//airport controls
interface AirportControlState {
  value: Airport | undefined;
  disabled: boolean;
}

// const doAirportsMatch = (a: Airport | undefined | null, b: Airport | undefined | null): boolean => {
//   if (!a && !b) {
//     return true;
//   } //both are null/undefined
//   if (!(a && b)) {
//     return false;
//   } //one is null/undefined but the other isnt
//   return a.airportId === b.airportId;
// };

const newAirportFormControl = (
  airportControlState: AirportControlState,
  airportValidators: ValidatorFn[]
) => {
  const control = new FormControl(airportControlState.value, airportValidators);
  if (airportControlState.disabled) {
    // Disable UI control, but still allow for programmatic value updates.
    control.disable({ emitEvent: false });
  }
  return control;
};

// const generateAirportMustMatchValidator = (airportToMatch: Airport): ValidatorFn => control => {
//   const selectedAirport = control.value as Airport | null | undefined;
//   if (!doAirportsMatch(selectedAirport, airportToMatch)) {
//     const validationErrors: ValidationErrors = {};
//     validationErrors['airportMismatch'] = true;
//     return validationErrors;
//   }
//   return null;
// };

export class LegForm {
  postedFlightId: FormControl = new FormControl(0);
  legNumber: FormControl = new FormControl(0);
  departureDateTime: FormControl = new FormControl('', [
    Validators.required,
    MaxDate(moment().endOf('day'))
  ]);
  arrivalDateTime: FormControl = new FormControl('', [Validators.required]);
  partOperation = new UntypedFormControl(null);

  legHobbs: FormGroup = new FormGroup(new LegHobbsForm());
  legOooi: FormGroup = new UntypedFormGroup(new LegOooiForm());
  legFuel: FormGroup = new UntypedFormGroup(new LegFuelForm());
  delayTypeId: FormControl = new FormControl();
  delayDuration: FormControl = new FormControl(0, [
    Validators.pattern('[0-9]+'),
    Validators.min(1)
  ]);
  goArounds: FormControl = new FormControl(0, [Validators.pattern('[0-9]+'), Validators.min(0)]);
  rejectedTakeoffs: FormControl = new FormControl(0, [
    Validators.pattern('[0-9]+'),
    Validators.min(0)
  ]);
  newLanding: FormControl = new FormControl('');
  quantityTypeId: FormControl = new FormControl(undefined);
  upliftQuantityTypeId: FormControl = new FormControl(null);
  plannedUp: FormControl = new FormControl(0, [Validators.min(0)]);
  actualUp: FormControl = new FormControl(0, [Validators.min(0)]);
  notes: FormControl = new FormControl('');
  crewNotes: FormControl = new FormControl('');
  paxNotes: FormControl = new FormControl('');
  departureFboNotes: FormControl = new FormControl('');
  arrivalFboNotes: FormControl = new FormControl('');
  automatedData: FormGroup | null;
  // flags
  isTechStop = new FormControl(false);
  isVerified: FormControl = new FormControl('');
  showHobbs: FormControl = new FormControl('');
  // FKs
  aircraftId: FormControl = new FormControl(0);
  tripId: FormControl = new FormControl(0);
  businessCategoryId: FormControl = new FormControl(0);
  departmentId: FormControl = new FormControl(0);
  scheduledFlightId: FormControl = new FormControl(0);
  externalFlightId: FormControl = new FormControl(0);
  // Hydraulic Fluids & De-icing
  hydraulicUsage: FormControl = new FormControl('');
  hydraulicFluidUnitTypeId: FormControl = new FormControl('');
  deiceForm: FormGroup;
  // collections
  flightPassengers: FormArray = new UntypedFormArray([]);
  flightCrewMembers: FormArray = new UntypedFormArray([]);
  fluids: FormArray = new UntypedFormArray([]);
  expenses: FormArray = new UntypedFormArray([]);
  squawks: FormArray = new UntypedFormArray([]);
  postedFlightComponents: FormArray = new UntypedFormArray([]);
  //airports - these will be initialized in the constructor, since their validators depend on previous/next leg info
  departureAirport: FormControl;
  arrivalAirport: FormControl;
  departureFboId: FormControl = new FormControl(0);
  arrivalFboId: FormControl = new FormControl(0);
  legPurposeNotes: FormControl = new FormControl('');
  flightBusinessPersons: FormArray = new UntypedFormArray([]);

  // eslint-disable-line

  //TODO: init from model data
  constructor(leg?: Leg) {
    if (leg) {
      this.postedFlightId.setValue(leg.postedFlightId);
      this.partOperation.setValue(
        leg.canSelectPartOperation ? leg.partOperation || PartOperation.Part91 : null
      );

      const legnum = this.resolveLegNumber(leg);
      this.legNumber.setValue(legnum);
      this.showHobbs.setValue(leg.showHobbs);

      if (leg.showHobbs) {
        this.legOooi = new UntypedFormGroup(new LegOooiForm(leg, false));
        this.legHobbs = new FormGroup(new LegHobbsForm(leg, true), { validators: validateHobbs() });
      } else {
        this.legOooi = new UntypedFormGroup(new LegOooiForm(leg, true));
        this.legHobbs = new FormGroup(new LegHobbsForm(leg, false));
      }

      if (leg.scheduledFlightId && !leg.postedFlightId && leg.departureDateTime) {
        //todo: use existing logic for determining if this is a preflight leg
        //trying to minimize file changes as this is part of a hotfix
        this.departureDateTime.setValue(leg.departureDateTime);
      }
      this.legFuel = new UntypedFormGroup(new LegFuelForm(leg));

      this.deiceForm = new FormGroup(
        {
          deiceEndDateTime: new FormControl(''),
          deiceStartDateTime: new FormControl(''),
          deiceEndTime: new FormControl(''),
          deiceStartTime: new FormControl(''),
          deiceMixRatioTypeId: new FormControl(''),
          deiceTypeId: new FormControl('')
        },
        validateDeice()
      );

      this.delayTypeId.setValue(leg.delayTypeId);
      this.delayDuration.setValue(leg.delayDuration);
      this.rejectedTakeoffs.setValue(leg.rejectedTakeoffs);
      this.goArounds.setValue(leg.goArounds);
      this.plannedUp.setValue(leg.plannedUp);
      this.actualUp.setValue(leg.actualUp);
      this.upliftQuantityTypeId.setValue(leg.upliftQuantityTypeId);
      this.quantityTypeId.setValue(leg.quantityTypeId);
      this.notes.setValue(leg.notes);
      this.crewNotes.setValue(leg.crewNotes);
      this.paxNotes.setValue(leg.paxNotes);
      this.departureFboNotes.setValue(leg.departureFboNotes);
      this.arrivalFboNotes.setValue(leg.arrivalFboNotes);
      if (leg.newLanding) {
        this.newLanding.setValue(leg.newLanding !== 0 ? leg.newLanding : 1);
      } else {
        this.newLanding.setValue(1);
      }
      if (leg.automatedData) {
        this.automatedData = new UntypedFormGroup(new AutomatedFlightDataForm(leg.automatedData));
      }

      // flags
      this.isTechStop.setValue(leg.isTechStop);
      this.isVerified.setValue(leg.isVerified);
      // FKs
      this.aircraftId.setValue(leg.aircraftId);
      this.tripId.setValue(leg.tripId);
      this.businessCategoryId.setValue(leg.businessCategoryId);
      this.departmentId.setValue(leg.departmentId);
      this.scheduledFlightId.setValue(leg.scheduledFlightId);
      this.externalFlightId.setValue(leg.externalFlightId);

      this.departureFboId.setValue(leg.departureFboId);
      this.arrivalFboId.setValue(leg.arrivalFboId);
      this.legPurposeNotes.setValue(leg.legPurposeNotes);

      const airportValidators: ValidatorFn[] = [Validators.required];
      //departure airport
      const departureAirportControlState: AirportControlState = {
        value: undefined,
        disabled: false
      };
      departureAirportControlState.value = leg.departureAirport || undefined;
      this.departureAirport = newAirportFormControl(
        departureAirportControlState,
        airportValidators
      );

      //arrival airport
      const arrivalAirportControlState: AirportControlState = {
        value: leg.arrivalAirport || undefined,
        //if next leg exists, disable the control
        disabled: false
      };
      this.arrivalAirport = newAirportFormControl(arrivalAirportControlState, airportValidators);
      //end airports

      this.deiceForm.patchValue({
        deiceEndDateTime: leg.deIceEndDateTime,
        deiceStartDateTime: leg.deIceStartDateTime,
        deiceEndTime: leg.deIceEndDateTime,
        deiceStartTime: leg.deIceStartDateTime,
        deiceMixRatioTypeId: leg.deIceMixRatioTypeId,
        deiceTypeId: leg.deIceTypeId,
        deiceTestOne: leg.deiceTestOne
      });

      this.initUpliftControls();
    }
  }

  private initUpliftControls() {
    if (this.plannedUp) {
      this.plannedUp.valueChanges.subscribe(() => {
        this.verifyUpliftValues();
      });
    }

    if (this.actualUp) {
      this.actualUp.valueChanges.subscribe(() => {
        this.verifyUpliftValues();
      });
    }

    this.verifyUpliftValues();
  }

  private verifyUpliftValues() {
    const plannedUp = this.plannedUp.value ? this.plannedUp.value : undefined;
    const actualUp = this.actualUp.value ? this.actualUp.value : undefined;
    let validatorsChanged = false;

    if (plannedUp !== undefined && actualUp !== undefined) {
      if (this.quantityTypeId.validator === null) {
        if (!this.quantityTypeId.value) {
          this.quantityTypeId.enable();
          this.quantityTypeId.setValue(2);
        }
        this.quantityTypeId.setValidators(Validators.required);
        validatorsChanged = true;
      }
    }

    if (validatorsChanged) {
      this.quantityTypeId.updateValueAndValidity({ emitEvent: true });
      this.quantityTypeId.markAsTouched({ onlySelf: true });
    }
  }

  //NOTE: attempting to workaround data where leg stored as legNumber 0
  private resolveLegNumber(leg: Leg) {
    const { customTripId, legNumber } = leg;
    return (legNumber || 0) < 1 && customTripId ? 1 : legNumber;
  }
}

// validators
export function validateDeice(): ValidatorFn {
  return (deiceForm: FormGroup): ValidationErrors | null => {
    const deiceEndDateTimeControl = deiceForm.get('deiceEndDateTime');
    const deiceStartDateTimeControl = deiceForm.get('deiceStartDateTime');
    const deiceEndTimeControl = deiceForm.get('deiceEndTime');
    const deiceStartTimeControl = deiceForm.get('deiceStartTime');

    let deiceEndDateTime: moment.Moment | null = null;
    let deiceStartDateTime: moment.Moment | null = null;
    let deiceEndTime: moment.Moment | null = null;
    let deiceStartTime: moment.Moment | null = null;

    if (deiceEndDateTimeControl) {
      deiceEndDateTimeControl.setErrors(null);
      deiceEndDateTime = moment.utc(deiceEndDateTimeControl.value);
    }
    if (deiceStartDateTimeControl) {
      deiceStartDateTimeControl.setErrors(null);
      deiceStartDateTime = moment.utc(deiceStartDateTimeControl.value);
    }
    if (deiceEndTimeControl) {
      deiceEndTimeControl.setErrors(null);
      deiceEndTime = moment.utc(deiceEndTimeControl.value || 0);
    }
    if (deiceStartTimeControl) {
      deiceStartTimeControl.setErrors(null);
      deiceStartTime = moment.utc(deiceStartTimeControl.value || 0);
    }

    //require date if time is entered
    if (
      deiceStartTimeControl &&
      deiceStartTimeControl.value &&
      deiceStartDateTimeControl &&
      !deiceStartDateTimeControl.value
    ) {
      deiceStartDateTimeControl.setErrors({ required: '' });
      deiceStartDateTimeControl.markAsTouched();
      return {};
    }
    if (
      deiceEndTimeControl &&
      deiceEndTimeControl.value &&
      deiceEndDateTimeControl &&
      !deiceEndDateTimeControl.value
    ) {
      deiceEndDateTimeControl.setErrors({ required: '' });
      deiceEndDateTimeControl.markAsTouched();
      return {};
    }

    if (
      (deiceStartDateTimeControl && !deiceStartDateTimeControl.value) ||
      (deiceEndDateTimeControl && !deiceEndDateTimeControl.value)
    ) {
      return null;
    }

    //start must be before end
    if (
      deiceEndDateTime &&
      deiceStartDateTime &&
      deiceStartTime &&
      deiceEndTime &&
      deiceEndDateTimeControl &&
      deiceEndTimeControl &&
      deiceStartTimeControl
    ) {
      const days = deiceEndDateTime.diff(deiceStartDateTime, 'd');
      if (days < 0) {
        deiceEndDateTimeControl.setErrors({ endDateBeforeStart: '' });
        if (deiceEndDateTimeControl.value) {
          deiceEndDateTimeControl.markAsTouched();
        }
        return {};
      } else if (days === 0) {
        const time = deiceEndDateTime
          .hour(deiceEndTime.hour())
          .minute(deiceEndTime.minute())
          .diff(deiceStartDateTime.hour(deiceStartTime.hour()).minute(deiceStartTime.minute()));
        if (time < 0) {
          deiceEndDateTimeControl.setErrors({ endDateBeforeStart: '' });
          if (deiceEndDateTimeControl.value) {
            deiceEndDateTimeControl.markAsTouched();
          }
          return {};
        }
      }
    }

    return null;
  };
}
